/**
 * HUB-C — Données paiements pour onglet Centre Activité (SumUp + Stripe + preuves + retraits).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildTimelineEvents,
  filterSumupByStatus,
  isInDateRange,
  matchesPaymentSearch,
} from '../../utils/paiements/paiementsDisplay';

export default function useActivitePaiements(api, filters, { enabled = true } = {}) {
  const [sumupPayments, setSumupPayments] = useState([]);
  const [stripePayments, setStripePayments] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [proofOrders, setProofOrders] = useState([]);
  const [retraits, setRetraits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sumupStatusFilter, setSumupStatusFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    if (!api || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const histParams = new URLSearchParams({ page: '1', limit: '100' });
      if (filters.vendeur) histParams.append('vendeur', filters.vendeur);
      if (filters.date_debut) histParams.append('date_debut', filters.date_debut);
      if (filters.date_fin) histParams.append('date_fin', filters.date_fin);
      if (filters.search) histParams.append('search', filters.search);
      if (filters.magasin_id) histParams.append('magasin_id', filters.magasin_id);

      const [sumupRes, pendingRes, stripeRes, ordersRes, histRes] = await Promise.all([
        api.get('/payments/sumup/all'),
        api.get('/payments/sumup/pending'),
        api.get('/payments/stripe/sales'),
        api.get('/boutique/commandes'),
        api.get(`/ventes/historique?${histParams.toString()}`),
      ]);

      setSumupPayments(sumupRes.data || []);
      setPendingPayments(pendingRes.data || []);
      setStripePayments(stripeRes.data || []);
      const ordersWithProof = (ordersRes.data || []).filter(
        (o) => o.payment_proof || o.statut === 'proof_uploaded',
      );
      setProofOrders(ordersWithProof);
      const histVentes = histRes.data?.ventes || [];
      setRetraits(histVentes.filter((v) => v.type === 'retrait'));
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur chargement paiements');
      setSumupPayments([]);
      setStripePayments([]);
      setPendingPayments([]);
      setProofOrders([]);
      setRetraits([]);
    } finally {
      setLoading(false);
    }
  }, [api, enabled, filters]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filteredSumup = useMemo(() => {
    let list = sumupPayments.filter(
      (p) => isInDateRange(p.created_at, filters.date_debut, filters.date_fin)
        && matchesPaymentSearch(p, filters.search, { kind: 'sumup' }),
    );
    list = filterSumupByStatus(list, sumupStatusFilter);
    return list;
  }, [sumupPayments, filters, sumupStatusFilter]);

  const filteredStripe = useMemo(() => (
    stripePayments.filter(
      (p) => isInDateRange(p.created_at, filters.date_debut, filters.date_fin)
        && matchesPaymentSearch(p, filters.search, { kind: 'stripe' }),
    )
  ), [stripePayments, filters]);

  const filteredProofs = useMemo(() => (
    proofOrders.filter((o) => {
      const date = o.payment_proof?.uploaded_at || o.created_at;
      return isInDateRange(date, filters.date_debut, filters.date_fin)
        && matchesPaymentSearch(o, filters.search, { kind: 'proof' });
    })
  ), [proofOrders, filters]);

  const filteredRetraits = useMemo(() => retraits, [retraits]);

  const timeline = useMemo(
    () => buildTimelineEvents({
      sumupPayments: filteredSumup,
      stripePayments: filteredStripe,
      limit: 100,
    }),
    [filteredSumup, filteredStripe],
  );

  const kpis = useMemo(() => {
    const pendingCount = sumupPayments.filter((p) => ['PAID_PENDING', 'PENDING'].includes(p.status)).length
      + stripePayments.filter((p) => p.status === 'PENDING').length;
    const proofPendingCount = proofOrders.filter((o) => o.payment_proof && !o.payment_proof.validated).length;
    const retraitsTotal = filteredRetraits.reduce(
      (acc, r) => acc + Math.abs(Number(r.prix_total ?? r.total ?? 0)),
      0,
    );
    const sumupConfirmed = filteredSumup.filter((p) => p.status === 'PAID_APPLIED').length;
    const stripeCompleted = filteredStripe.filter((p) => p.status === 'COMPLETED').length;

    return {
      pendingCount,
      proofPendingCount,
      retraitsTotal,
      sumupCount: filteredSumup.length,
      stripeCount: filteredStripe.length,
      sumupConfirmed,
      stripeCompleted,
      timelineCount: timeline.length,
    };
  }, [sumupPayments, stripePayments, proofOrders, filteredSumup, filteredStripe, filteredRetraits, timeline]);

  return {
    sumupPayments: filteredSumup,
    stripePayments: filteredStripe,
    pendingPayments,
    proofOrders: filteredProofs,
    retraits: filteredRetraits,
    timeline,
    kpis,
    loading,
    error,
    sumupStatusFilter,
    setSumupStatusFilter,
    refresh: fetchAll,
  };
}
